// Image detail of one OCI repository (ADR-0033 section 12, api.md 4.1, registry 0.18.0): tags, manifests with
// platforms and referrers, pull commands, retention settings, and deletes with the ADR-0031 grace period.
import { useState, type FormEvent } from "react";
import type {
  OciManifest,
  OciManifestRestored,
  OciRepoSettings,
  OciRepository,
  OciTag,
  Pending,
  TenantDetail,
} from "../api/types";
import { useServices } from "../components/context";
import { Id } from "../components/IdCell";
import { PendingFilter, useGrace, type PendingRow } from "../components/Grace";
import { CodeBlock, ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatBytes, formatTime } from "../lib/format";
import { routeHash } from "../lib/router";
import { atLeast } from "../lib/roles";

/** The delete dialog text for a container repository (ADR-0031: the admin types the repository name). */
export function repoDeleteTarget(tenant: string, r: { id: string; namespace: string; name: string }) {
  return {
    id: r.id,
    title: `repository ${tenant}/${r.namespace}/${r.name}`,
    message: "All its tags and manifests are deleted too, and pulls of it fail.",
    typedName: r.name,
    typedLabel: "repository name",
  };
}

const MEDIA_LABEL: Record<string, string> = {
  "application/vnd.oci.image.manifest.v1+json": "image",
  "application/vnd.oci.image.index.v1+json": "index",
  "application/vnd.docker.distribution.manifest.v2+json": "image (Docker)",
  "application/vnd.docker.distribution.manifest.list.v2+json": "index (Docker)",
};

/** A short kind for a manifest: the artifact type of a referrer (a signature or an SBOM), else image or index. */
export function manifestKind(m: Pick<OciManifest, "media_type" | "artifact_type">): string {
  return m.artifact_type ?? MEDIA_LABEL[m.media_type] ?? m.media_type;
}

/** `sha256:` and the first 12 hex digits, as Docker shows a digest. */
export function shortDigest(digest: string): string {
  const i = digest.indexOf(":");
  return i < 0 ? digest.slice(0, 12) : `${digest.slice(0, i + 1)}${digest.slice(i + 1, i + 13)}`;
}

/** The tag for the pull example: `latest` if it exists, else the newest push. */
export function exampleTag(tags: OciTag[]): OciTag | undefined {
  return (
    tags.find((t) => t.tag === "latest") ?? [...tags].sort((a, b) => (a.pushed_at < b.pushed_at ? 1 : -1))[0]
  );
}

/** Text for the toast after a manifest restore (Q29 G4: tags pushed again meanwhile stay where they are). */
export function manifestRestoreText(
  r: Pick<OciManifestRestored, "restored_tags" | "not_restored_tags">,
): string {
  const lines: string[] = [];
  if (r.restored_tags.length) lines.push(`Tags restored: ${r.restored_tags.join(", ")}.`);
  if (r.not_restored_tags.length)
    lines.push(`Pushed again since the delete, so not moved back: ${r.not_restored_tags.join(", ")}.`);
  return lines.join(" ");
}

export function ImageDetail({ tenant, repoId }: { tenant: TenantDetail; repoId: string }) {
  const { api, config } = useServices();
  const detail = useLoad(() => api.getOciRepository(tenant.slug, repoId), [tenant.slug, repoId]);
  const [gone, setGone] = useState(false);
  const back = routeHash({ name: "tenant", tenant: tenant.slug, page: "packages", type: "oci" });

  const repoGrace = useGrace<{ id: string }>({
    tenant,
    onChange: detail.reload,
    ops: {
      key: `oci-repository:${repoId}`,
      listPending: async () => ({ items: [] }),
      remove: async (t) => {
        const res = await api.deleteOciRepository(tenant.slug, t.id, t.typedName ?? "");
        setGone(true);
        return res;
      },
      restore: async (id) => {
        await api.restoreOciRepository(tenant.slug, id);
        setGone(false);
      },
      purge: (id, confirm) => api.purgeOciRepository(tenant.slug, id, confirm),
    },
  });

  const repo = detail.data;
  if (gone) {
    return (
      <>
        <Section title="Repository deleted">
          <p className="muted">
            The repository is pending deletion. Restore it with Undo, or from "Pending deletion" in{" "}
            <a href={back}>Containers</a>.
          </p>
        </Section>
        {repoGrace.ui}
      </>
    );
  }
  if (!repo) {
    return (
      <Section title="Image">
        <p>
          <a href={back}>All containers</a>
        </p>
        <ErrorBanner error={detail.error} />
        {detail.loading && <p className="muted">Loading...</p>}
      </Section>
    );
  }
  return (
    <>
      <RepoHeader
        tenant={tenant}
        repo={repo}
        ociHost={config.ociHost}
        back={back}
        onDelete={
          repoGrace.isAdmin ? () => repoGrace.askDelete(repoDeleteTarget(tenant.slug, repo)) : undefined
        }
        busy={repoGrace.busy}
        error={repoGrace.error}
      />
      <RepoParts tenant={tenant} repo={repo} reload={detail.reload} />
      <RepoSettings key={JSON.stringify(repo.settings)} tenant={tenant} repo={repo} onSaved={detail.reload} />
      {repoGrace.ui}
    </>
  );
}

function RepoHeader({
  tenant,
  repo,
  ociHost,
  back,
  onDelete,
  busy,
  error,
}: {
  tenant: TenantDetail;
  repo: OciRepository;
  ociHost: string;
  back: string;
  onDelete?: () => void;
  busy: boolean;
  error: unknown;
}) {
  const image = `${ociHost}/${repo.full_name}`;
  const tag = exampleTag(repo.tags);
  const pull = tag
    ? [`docker pull ${image}:${tag.tag}`, `docker pull ${image}@${tag.digest}`, ""].join("\n")
    : `# No tags yet. Push one with: docker push ${image}:<tag>\n`;
  return (
    <Section
      title={repo.name}
      actions={
        onDelete && (
          <button type="button" className="danger" disabled={busy} onClick={onDelete}>
            Delete repository
          </button>
        )
      }
    >
      <p>
        <a href={back}>All containers</a>
      </p>
      <ErrorBanner error={error} />
      <dl className="ns-facts">
        <dt>Image name</dt>
        <dd>
          <code data-testid="full-name">{image}</code>
        </dd>
        <dt>Namespace</dt>
        <dd>
          <a href={routeHash({ name: "tenant", tenant: tenant.slug, page: "grants", ns: repo.namespace })}>
            {repo.namespace}
          </a>
        </dd>
        <dt>Created</dt>
        <dd>{formatTime(repo.created_at)}</dd>
        <dt>Last push</dt>
        <dd>{formatTime(repo.last_push_at)}</dd>
        <dt>ID</dt>
        <dd>
          <Id id={repo.id} />
        </dd>
      </dl>
      <CodeBlock title="Pull with the tag, or with the digest" code={pull} />
    </Section>
  );
}

function RepoParts({
  tenant,
  repo,
  reload,
}: {
  tenant: TenantDetail;
  repo: OciRepository;
  reload: () => void;
}) {
  const { api } = useServices();
  const pendingRepo = () => api.getOciRepository(tenant.slug, repo.id, true);
  const tagGrace = useGrace<OciTag & { id: string }>({
    tenant,
    onChange: () => {
      reload();
      manifestGrace.reloadPending();
    },
    ops: {
      key: `oci-tags:${repo.id}`,
      listPending: async () => ({
        items: (await pendingRepo()).tags.map(
          (t) => ({ ...t, id: t.tag }) as Pending<OciTag & { id: string }>,
        ),
      }),
      remove: (t) => api.deleteOciPart(tenant.slug, repo.id, { kind: "tag", tag: t.id }),
      restore: async (id) => void (await api.restoreOciPart(tenant.slug, repo.id, { kind: "tag", tag: id })),
      purge: (id, confirm) => api.purgeOciPart(tenant.slug, repo.id, { kind: "tag", tag: id }, confirm),
    },
  });
  const manifestGrace = useGrace<OciManifest>({
    tenant,
    onChange: () => {
      reload();
      tagGrace.reloadPending();
    },
    ops: {
      key: `oci-manifests:${repo.id}`,
      listPending: async () => ({
        items: (await pendingRepo()).manifests.map((m) => ({ ...m, id: m.digest }) as Pending<OciManifest>),
      }),
      remove: (t) => api.deleteOciPart(tenant.slug, repo.id, { kind: "manifest", digest: t.id }),
      restore: async (id) =>
        manifestRestoreText(
          (await api.restoreOciPart(tenant.slug, repo.id, {
            kind: "manifest",
            digest: id,
          })) as OciManifestRestored,
        ),
      purge: (id, confirm) =>
        api.purgeOciPart(tenant.slug, repo.id, { kind: "manifest", digest: id }, confirm),
    },
  });
  const isAdmin = tagGrace.isAdmin;
  const name = `${repo.full_name}`;

  const tagRows: PendingRow[] = tagGrace.items.map((t) => ({
    id: t.tag,
    title: <span className="mono">{t.tag}</span>,
    detail: <span className="mono">{shortDigest(t.digest)}</span>,
    deletedBy: t.deleted_by,
    deletedAt: t.deleted_at,
    purgeAfter: t.purge_after,
    purge: { id: t.tag, title: `tag ${name}:${t.tag}`, confirm: t.tag, confirmLabel: "tag" },
  }));
  const manifestRows: PendingRow[] = manifestGrace.items.map((m) => ({
    id: m.digest,
    title: <span className="mono">{shortDigest(m.digest)}</span>,
    detail: `${manifestKind(m)}, ${formatBytes(m.size_bytes)}`,
    deletedBy: m.deleted_by,
    deletedAt: m.deleted_at,
    purgeAfter: m.purge_after,
    purge: {
      id: m.digest,
      title: `manifest ${name}@${shortDigest(m.digest)}`,
      confirm: m.digest,
      confirmLabel: "full manifest digest",
    },
  }));

  return (
    <>
      <Section
        title="Tags"
        actions={<PendingFilter view={tagGrace.view} setView={tagGrace.setView} count={tagGrace.count} />}
      >
        <ErrorBanner error={tagGrace.error} />
        {tagGrace.view === "pending" ? (
          tagGrace.rowsTable(tagRows)
        ) : repo.tags.length === 0 ? (
          <p className="muted">No tags.</p>
        ) : (
          <table className="stack data-table" aria-label="Tags">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Digest</th>
                <th>Size</th>
                <th>Pushed</th>
                <th>By</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {repo.tags.map((t) => (
                <tr key={t.tag}>
                  <td className="mono">
                    <strong>{t.tag}</strong>
                  </td>
                  <td className="mono" data-label="Digest" title={t.digest}>
                    {shortDigest(t.digest)}
                  </td>
                  <td data-label="Size">{formatBytes(t.size_bytes)}</td>
                  <td data-label="Pushed">{formatTime(t.pushed_at)}</td>
                  <td data-label="By">{t.pushed_by ? <Id id={t.pushed_by} /> : "unknown"}</td>
                  <td className="actions" data-label="Actions">
                    {isAdmin && (
                      <button
                        type="button"
                        className="danger"
                        aria-label={`Delete tag ${t.tag}`}
                        disabled={tagGrace.busy}
                        onClick={() =>
                          tagGrace.askDelete({
                            id: t.tag,
                            title: `tag ${name}:${t.tag}`,
                            message: `Pulls of ${t.tag} fail. The manifest ${shortDigest(t.digest)} stays.`,
                          })
                        }
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      <Section
        title="Manifests"
        actions={
          <PendingFilter
            view={manifestGrace.view}
            setView={manifestGrace.setView}
            count={manifestGrace.count}
          />
        }
      >
        <p className="muted small">
          An index lists one image per platform. Referrers are signatures, SBOMs and other artifacts about a
          manifest.
        </p>
        <ErrorBanner error={manifestGrace.error} />
        {manifestGrace.view === "pending" ? (
          manifestGrace.rowsTable(manifestRows)
        ) : repo.manifests.length === 0 ? (
          <p className="muted">No manifests.</p>
        ) : (
          <table className="stack data-table" aria-label="Manifests">
            <thead>
              <tr>
                <th>Digest</th>
                <th>Kind</th>
                <th>Tags</th>
                <th>Platforms</th>
                <th>Referrers</th>
                <th>Size</th>
                <th>Pushed</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {repo.manifests.map((m) => (
                <tr key={m.digest}>
                  <td className="mono" title={m.digest}>
                    {shortDigest(m.digest)}
                    {m.subject && <small className="muted"> refers to {shortDigest(m.subject)}</small>}
                  </td>
                  <td data-label="Kind">{manifestKind(m)}</td>
                  <td className="mono" data-label="Tags">
                    {m.tags.join(", ") || "none"}
                  </td>
                  <td className="mono small" data-label="Platforms">
                    {m.platforms?.map((p) => p.platform).join(", ") || ""}
                  </td>
                  <td className="mono small" data-label="Referrers">
                    {m.referrers.map(shortDigest).join(", ") || ""}
                  </td>
                  <td data-label="Size">{formatBytes(m.size_bytes)}</td>
                  <td data-label="Pushed">{formatTime(m.pushed_at)}</td>
                  <td className="actions" data-label="Actions">
                    {isAdmin && (
                      <button
                        type="button"
                        className="danger"
                        aria-label={`Delete manifest ${shortDigest(m.digest)}`}
                        disabled={manifestGrace.busy}
                        onClick={() =>
                          manifestGrace.askDelete({
                            id: m.digest,
                            title: `manifest ${name}@${shortDigest(m.digest)}`,
                            message: m.tags.length
                              ? `Its tags ${m.tags.join(", ")} are deleted too. Pulls by these tags and by digest fail.`
                              : "Pulls by digest fail.",
                          })
                        }
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {repo.manifests_truncated && (
          <p className="muted small">This list shows the newest 1000 manifests.</p>
        )}
      </Section>
      {tagGrace.ui}
      {manifestGrace.ui}
    </>
  );
}

/** Retention and tag settings (ADR-0033 section 9). The registry checks the values and the patterns. */
function RepoSettings({
  tenant,
  repo,
  onSaved,
}: {
  tenant: TenantDetail;
  repo: OciRepository;
  onSaved: () => void;
}) {
  const { api } = useServices();
  const action = useAction();
  const s = repo.settings;
  const [mutability, setMutability] = useState<OciRepoSettings["tag_mutability"]>(s.tag_mutability);
  const [keep, setKeep] = useState(String(s.keep_tags));
  const [protectedTags, setProtectedTags] = useState(s.protected_tags.join("\n"));
  const [untagged, setUntagged] = useState(String(s.untagged_days));
  const isAdmin = atLeast(tenant.role, "admin");

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const body: Partial<OciRepoSettings> = {
      tag_mutability: mutability,
      keep_tags: Number(keep),
      protected_tags: protectedTags
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean),
      untagged_days: Number(untagged),
    };
    if (await action.run(async () => void (await api.updateOciRepository(tenant.slug, repo.id, body))))
      onSaved();
  }

  return (
    <Section title="Settings">
      <form className="form" onSubmit={onSave}>
        <label>
          Tags
          <select
            value={mutability}
            disabled={!isAdmin}
            onChange={(e) => setMutability(e.target.value as OciRepoSettings["tag_mutability"])}
          >
            <option value="mutable">Mutable: a push can move a tag</option>
            <option value="immutable">Immutable: a push cannot move a tag</option>
          </select>
        </label>
        <label>
          Keep tags (0 is off)
          <input
            type="number"
            min={0}
            value={keep}
            disabled={!isAdmin}
            onChange={(e) => setKeep(e.target.value)}
          />
          <small className="muted">
            When it is more than 0, the daily retention keeps this number of newest tags and moves older tags
            to "Pending deletion". Protected tags stay.
          </small>
        </label>
        <label>
          Protected tags (one pattern on each line)
          <textarea
            rows={3}
            spellCheck={false}
            value={protectedTags}
            disabled={!isAdmin}
            onChange={(e) => setProtectedTags(e.target.value)}
          />
          <small className="muted">Retention never moves a tag that matches one of these patterns.</small>
        </label>
        <label>
          Untagged manifests go to "Pending deletion" after (days)
          <input
            type="number"
            min={1}
            max={365}
            value={untagged}
            disabled={!isAdmin}
            onChange={(e) => setUntagged(e.target.value)}
          />
        </label>
        <ErrorBanner error={action.error} />
        {isAdmin && (
          <button type="submit" disabled={action.busy}>
            Save settings
          </button>
        )}
      </form>
    </Section>
  );
}
